import React, {
	useCallback,
	useState,
	useMemo,
	type SyntheticEvent,
	type ChangeEvent,
} from 'react';
import { styled } from '@compiled/react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import { Checkbox } from '@atlaskit/checkbox';
import ArrowRightIcon from '@atlaskit/icon/glyph/arrow-right';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import InlineDialog from '@atlaskit/inline-dialog';
import AkModalDialog, {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalTitle,
	type KeyboardOrMouseEvent,
} from '@atlaskit/modal-dialog';
import { Box, xcss } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';
import Textfield from '@atlaskit/textfield';
import { colors } from '@atlaskit/theme';
import { Y300 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import { AnalyticsSource, ViewTracker } from '@atlassian/jira-analytics-web-react/src';
import { ShortcutScope } from '@atlassian/jira-common-components-keyboard-shortcuts';
import { PRETTY } from '@atlassian/jira-common-constants';
// eslint-disable-next-line jira/restricted/@atlassian+jira-common-styles
import { gridSize } from '@atlassian/jira-common-styles/src';
import { useIntl } from '@atlassian/jira-intl';
import type { TimeTrackingConfig } from '@atlassian/jira-issue-shared-types';
import { timeStringToSeconds, isTimeStringValid } from '@atlassian/jira-issue-view-common-utils';
import WithValidationOnBlur from '@atlassian/jira-issue-view-common/src/component/with-validation-on-blur';
import timeMessages, {
	timeInvalidMessage,
} from '@atlassian/jira-issue-view-common/src/messages/time';
import { MODAL } from '@atlassian/jira-product-analytics-bridge';
import { timeTrackingFormatter } from '@atlassian/jira-time-tracking-formatter';
import type { Worklog } from '../../model';
import messages from './messages';

export type DispatchProps = {
	onConfirm: (
		timeRemaining: string,
		isAdjustTimeRemainingChecked: boolean,
		analyticsEvent: UIAnalyticsEvent,
	) => void;
	onCancel: (
		event: SyntheticEvent | KeyboardOrMouseEvent,
		analyticsEvent: UIAnalyticsEvent,
	) => void;
};

export type StateProps = {
	isDeleting: boolean;
	worklog: Worklog | null;
	timeTrackingConfig: TimeTrackingConfig;
	timeRemainingSeconds: number;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type OwnProps = Record<any, any>;

export type Props = DispatchProps & StateProps & OwnProps;

export const DeleteModal = AnalyticsSource(
	'deleteWorklog',
	MODAL,
)(({
	timeTrackingConfig,
	timeRemainingSeconds,
	worklog,
	onConfirm,
	onCancel,
	isDeleting,
}: Props) => {
	const intl = useIntl();

	// these methods need to be created before their use
	const secondsToTimeString = useCallback(
		(seconds: number) => {
			// At this time we need to force the value into an english formatted
			// time string eg: `1w 1d 1h 1m` as that is the only format supported
			// on entry, future changes will support localised values.
			const intlForTimeTrackingInput = {
				...intl,
				formatMessage: ({ defaultMessage }: { defaultMessage?: string }) => defaultMessage || '',
			};
			return timeTrackingFormatter(
				seconds,
				{
					workingHoursPerDay: timeTrackingConfig.hoursPerDay,
					workingDaysPerWeek: timeTrackingConfig.daysPerWeek,
					timeFormat: timeTrackingConfig.format || PRETTY,
					defaultUnit: timeTrackingConfig.defaultUnit,
				},
				intlForTimeTrackingInput,
			);
		},
		[
			intl,
			timeTrackingConfig.daysPerWeek,
			timeTrackingConfig.defaultUnit,
			timeTrackingConfig.format,
			timeTrackingConfig.hoursPerDay,
		],
	);

	const [lTimeStringToSeconds] = useState(() =>
		timeStringToSeconds(timeTrackingConfig.daysPerWeek, timeTrackingConfig.hoursPerDay),
	);
	const [originalTimeRemainingSeconds] = useState(() => timeRemainingSeconds);
	const [isAdjustTimeRemainingChecked, setIsAdjustTimeRemainingChecked] = useState(true);
	const [timeRemaining, setTimeRemaining] = useState(() =>
		secondsToTimeString(
			timeRemainingSeconds + Number(lTimeStringToSeconds(worklog?.timeSpent || '0s')),
		),
	);

	const isTimeRemainingValid = useMemo(() => isTimeStringValid(timeRemaining), [timeRemaining]);

	const onTimeRemainingChange = useCallback(
		(event: ChangeEvent<HTMLInputElement>) => setTimeRemaining(event.target.value),
		[],
	);

	const onCheckboxChange = useCallback(
		() => setIsAdjustTimeRemainingChecked(!isAdjustTimeRemainingChecked),
		[isAdjustTimeRemainingChecked],
	);

	const handleConfirm = useCallback(
		(event: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) =>
			onConfirm(
				isAdjustTimeRemainingChecked ? timeRemaining : secondsToTimeString(timeRemainingSeconds),
				isAdjustTimeRemainingChecked,
				analyticsEvent,
			),
		[
			isAdjustTimeRemainingChecked,
			timeRemainingSeconds,
			onConfirm,
			timeRemaining,
			secondsToTimeString,
		],
	);

	const timeRemainingInput = useMemo(
		() => (
			<WithValidationOnBlur isValid={isTimeRemainingValid}>
				{(shouldAppearInvalid) => (
					<>
						<TimeRemainingLabelWrapper>
							<label htmlFor="time-input">{intl.formatMessage(messages.adjustNewLabel)}</label>
							<RequiredIndicator role="presentation">*</RequiredIndicator>
						</TimeRemainingLabelWrapper>
						<InlineDialog
							content={timeInvalidMessage}
							isOpen={shouldAppearInvalid}
							placement="right"
						>
							<Textfield
								name="time-input"
								value={timeRemaining}
								// eslint-disable-next-line jira/integration/test-id-by-folder-structure
								testId="issue.activity.worklog.delete-modal.textfield"
								isInvalid={shouldAppearInvalid}
								onChange={onTimeRemainingChange}
								placeholder={intl.formatMessage(timeMessages.timeInputPlaceholder)}
								isDisabled={isDeleting}
								isRequired
								elemAfterInput={
									shouldAppearInvalid === true ? (
										<Box xcss={warningWrapperStyles}>
											<WarningIcon
												size="medium"
												primaryColor={token('color.icon.warning', Y300)}
												label=""
											/>
										</Box>
									) : null
								}
								id="time-input"
							/>
						</InlineDialog>
					</>
				)}
			</WithValidationOnBlur>
		),
		[intl, isDeleting, isTimeRemainingValid, onTimeRemainingChange, timeRemaining],
	);

	const content = useMemo(
		() => (
			<>
				<p>{intl.formatMessage(messages.deleteWarning)}</p>
				<CheckboxContainer>
					<Checkbox
						isChecked={isAdjustTimeRemainingChecked}
						isDisabled={isDeleting}
						onChange={onCheckboxChange}
						label={intl.formatMessage(messages.adjustCheckboxLabel)}
						name="adjust-checkbox"
						// eslint-disable-next-line jira/integration/test-id-by-folder-structure
						testId="issue.activity.worklog.delete-modal.checkbox"
					/>
				</CheckboxContainer>

				{isAdjustTimeRemainingChecked && (
					<AdjustContainer data-testid="issue-view-activity.worklog.delete-modal.container">
						<CurrentTimeRemainingContainer>
							<TimeRemainingLabelWrapper>
								{intl.formatMessage(messages.adjustCurrentLabel)}
							</TimeRemainingLabelWrapper>
							<CurrentTimeRemainingValue>{originalTimeRemainingSeconds}</CurrentTimeRemainingValue>
						</CurrentTimeRemainingContainer>
						<Box xcss={arrowContainerStyles}>
							{/* @ts-expect-error - TS2769 - No overload matches this call. */}
							<ArrowRightIcon />
						</Box>
						<NewTimeRemainingContainer>
							{timeRemainingInput}
							<Subtext>
								{intl.formatMessage(messages.adjustNewSubtext, {
									timeSpent: worklog?.timeSpent || '0s',
								})}
							</Subtext>
						</NewTimeRemainingContainer>
					</AdjustContainer>
				)}
			</>
		),
		[
			intl,
			isAdjustTimeRemainingChecked,
			isDeleting,
			onCheckboxChange,
			originalTimeRemainingSeconds,
			timeRemainingInput,
			worklog,
		],
	);

	const renderSpinner = () => (
		<Spinner interactionName="issue-worklog-delete-modal-spinner" size="small" />
	);

	return (
		<>
			<ViewTracker />
			<ShortcutScope>
				<AkModalDialog
					width="small"
					onClose={onCancel}
					shouldCloseOnEscapePress
					// eslint-disable-next-line jira/integration/test-id-by-folder-structure
					testId="issue.activity.worklog.delete-modal.ak-modal-dialog"
				>
					<ModalHeader>
						<ModalTitle appearance="danger">
							{intl.formatMessage(messages.deleteHeading)}
						</ModalTitle>
					</ModalHeader>
					<ModalBody>{content}</ModalBody>
					<ModalFooter>
						<Button
							interactionName="issue-worklog"
							iconAfter={isDeleting ? renderSpinner() : undefined}
							appearance="primary"
							onClick={handleConfirm}
							isDisabled={isDeleting || (isAdjustTimeRemainingChecked && !isTimeRemainingValid)}
							// eslint-disable-next-line jira/integration/test-id-by-folder-structure
							testId="issue.activity.worklog.delete-modal.confirm-button"
						>
							{intl.formatMessage(messages.delete)}
						</Button>
						<Button
							appearance="subtle"
							onClick={onCancel}
							isDisabled={isDeleting}
							// eslint-disable-next-line jira/integration/test-id-by-folder-structure
							testId="issue.activity.worklog.delete-modal.cancel-button"
							interactionName="auto-generated.issue.activity.worklog.delete-modal.cancel-button.button"
						>
							{intl.formatMessage(messages.cancel)}
						</Button>
					</ModalFooter>
				</AkModalDialog>
			</ShortcutScope>
		</>
	);
});

export default DeleteModal;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CurrentTimeRemainingContainer = styled.div({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	maxWidth: `${gridSize * 11}px`,
});

// These styles have been copied from the field-base label styles
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TimeRemainingLabelWrapper = styled.div({
	color: `${token('color.text.subtlest', '#6b778c')}`,
	font: token('font.heading.xxsmall'),
	paddingTop: token('space.250', '20px'),
	paddingBottom: token('space.250', '20px'),
	paddingLeft: 0,
	paddingRight: 0,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CurrentTimeRemainingValue = styled.div({
	textAlign: 'center',
	padding: token('space.150', '12px'),
});

const arrowContainerStyles = xcss({
	marginTop: 'space.800',
	marginBottom: 'space.600',
	marginLeft: 'space.100',
	marginRight: 'space.200',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CheckboxContainer = styled.div({
	marginTop: token('space.200', '16px'),
	marginBottom: token('space.200', '16px'),
	marginLeft: token('space.negative.050', '-4px'),
	marginRight: 0,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const NewTimeRemainingContainer = styled.div({
	width: '50%',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Subtext = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	fontSize: `${gridSize * 1.25}px`,
	marginTop: token('space.050', '4px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtlest', colors.N90),
});

const warningWrapperStyles = xcss({
	marginLeft: 'space.negative.050',
	marginRight: 'space.050',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const RequiredIndicator = styled.span({
	color: `${token('color.text.danger', 'red')}`,
	paddingLeft: token('space.025', '2px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const AdjustContainer = styled.div({
	display: 'flex',
	marginTop: token('space.negative.300', '-24px'),
});
